import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { apiGet } from "@/lib/api-client";
import { asPercent } from "@/lib/format";
import { asArray, asRecord } from "@/lib/type-utils";

interface CreatorAggregate {
  name: string;
  markets: number;
  volume: number;
}

/**
 * Manifold has no creators endpoint — the leaderboard is aggregated
 * client-side from the trending-markets payload (creatorUsername/volume).
 */
function aggregateCreators(markets: Record<string, unknown>[]): CreatorAggregate[] {
  const byCreator = new Map<string, CreatorAggregate>();
  for (const market of markets) {
    const name = String(market.creatorUsername ?? market.creatorName ?? market.creator ?? "Unknown");
    const entry = byCreator.get(name) ?? { name, markets: 0, volume: 0 };
    entry.markets += 1;
    entry.volume += Number(market.volume ?? market.totalVolume ?? 0) || 0;
    byCreator.set(name, entry);
  }
  return [...byCreator.values()].sort((a, b) => b.volume - a.volume);
}

export function ManifoldPage() {
  const marketsQuery = useQuery({
    queryKey: ["manifold", "markets"],
    queryFn: () => apiGet<unknown>("/api/manifold/markets"),
    refetchInterval: 300_000,
  });

  const trendingQuery = useQuery({
    queryKey: ["manifold", "trending"],
    queryFn: () => apiGet<unknown>("/api/manifold/markets/trending"),
    refetchInterval: 600_000,
  });

  // Both endpoints wrap results in a {markets, count} envelope (backend/routers/manifold.py).
  const markets = asArray(asRecord(marketsQuery.data).markets);
  const trendingMarkets = asArray(asRecord(trendingQuery.data).markets);
  const creators = aggregateCreators(trendingMarkets);

  const totalVolume = markets.reduce((sum, m) => sum + Number(m.volume ?? m.totalVolume ?? 0), 0);
  const totalTraders = markets.reduce((sum, m) => sum + Number(m.uniqueTraders ?? m.traders ?? 0), 0);
  const avgProbability =
    markets.length > 0
      ? markets.reduce((sum, m) => sum + Number(m.probability ?? m.p ?? 0.5), 0) / markets.length
      : null;

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="Manifold Markets"
        description="Play-money prediction market with creator economy metrics."
      >
        {marketsQuery.isError ? (
          <QueryErrorState what="Manifold market data" onRetry={() => void marketsQuery.refetch()} />
        ) : (
          <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
            <StatCard label="Active Markets" value={String(markets.length)} hint="Manifold markets tracked" />
            <StatCard label="Total Volume" value={`${totalVolume.toLocaleString()}`} hint="Mana (play money)" />
            <StatCard label="Unique Traders" value={String(totalTraders)} hint="Across all markets" />
            <StatCard
              label="Avg Probability"
              value={avgProbability !== null ? asPercent(avgProbability) : "—"}
              hint="Mean market probability"
            />
          </div>
        )}
      </Panel>

      <Panel title="Trending Markets" description="Popular markets on Manifold">
        <div className="max-h-[400px] space-y-2 overflow-y-auto">
          {marketsQuery.isError ? (
            <QueryErrorState what="Manifold market list" onRetry={() => void marketsQuery.refetch()} />
          ) : markets.length === 0 ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              No Manifold market data available
            </div>
          ) : (
            markets.slice(0, 20).map((market, index) => (
              <div key={index} className="rounded-lg bg-gunmetal/50 p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="text-sm font-medium">{String(market.question ?? market.title ?? "Unknown")}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                      <span>by {String(market.creatorUsername ?? market.creator ?? "Unknown")}</span>
                      <span>•</span>
                      <span>{Number(market.volume ?? 0).toLocaleString()} volume</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gold-400">
                      {asPercent(Number(market.probability ?? market.p ?? 0))}
                    </div>
                    <div className="text-xs text-slate-400">{Number(market.uniqueTraders ?? 0)} traders</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>

      <Panel title="Top Creators" description="Creators of trending markets, ranked by volume (aggregated client-side)">
        <div className="max-h-[300px] space-y-2 overflow-y-auto">
          {trendingQuery.isError ? (
            <QueryErrorState what="trending-market creator data" onRetry={() => void trendingQuery.refetch()} />
          ) : creators.length === 0 ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              No creator data available
            </div>
          ) : (
            creators.slice(0, 15).map((creator, index) => (
              <div key={creator.name} className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold-500/20 text-gold-400 font-bold">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium">{creator.name}</div>
                    <div className="text-xs text-slate-400">{creator.markets} markets</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gold-400">{creator.volume.toLocaleString()}</div>
                  <div className="text-xs text-slate-400">volume</div>
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Markets API" description="Raw Manifold market data">
          <JsonView value={marketsQuery.data} />
        </Panel>
        <Panel title="Trending API" description="Raw trending-market data">
          <JsonView value={trendingQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
